
import { EditorView } from '@codemirror/view'
import { HighlightStyle, syntaxHighlighting } from '@codemirror/language'
import { tags } from "@lezer/highlight";
// Single source of truth for these is the $themes map in functions/scss/_variables.scss, which
// compiles into --color-* custom properties on <html>. Referencing those rather than hex values is
// what lets the editor follow whichever theme the account picked on /user, switching live along
// with the rest of the page, with nothing to rebuild per theme.
const cssColor = (role) => `var(--color-${role})`;

const $color_white = cssColor('white');
const $color_white_l = cssColor('white-l');
const $color_white_h = cssColor('white-h');

const $color_green = cssColor('green');
const $color_green_h = cssColor('green-h');
const $color_blue = cssColor('blue');
const $color_blue_h = cssColor('blue-h');
const $color_red = cssColor('red');
const $color_red_h = cssColor('red-h');
const $color_gold = cssColor('gold');
// Translucent on purpose: .cm-selectionLayer below sits above the content layer, so the
// selection background has to be see-through or it hides the selected text.
const $color_gold_highlight = cssColor('gold-highlight');

const $color_dark = cssColor('dark');
const $color_dark_b = cssColor('dark-b');
const $color_dark_l = cssColor('dark-l');
const $color_dark_h = cssColor('dark-h');
const $color_dark_t = cssColor('dark-t');

// The editable editor is ink on paper in every theme, light or dark.
const $color_paper = cssColor('paper');
const $color_ink = cssColor('ink');

// Syntax accents, from $syntax-palettes in _variables.scss. The read-only cards use the set for
// the active theme's scheme (--syntax-*); the editable editor always sits on paper, so it always
// uses the light set (--syntax-paper-*).
const syntax = (role) => `var(--syntax-${role})`;
const syntaxPaper = (role) => `var(--syntax-paper-${role})`;

// The active-line tint: a theme colour at 0x70 (44%) alpha by default, mixed in CSS so it follows
// the theme.
const translucent = (color, percent = 44) => `color-mix(in srgb, ${color} ${percent}%, transparent)`;

// The Ctrl+F search panel is plain DOM injected by @codemirror/search, and its own base theme is
// written for a light editor sitting on a light page, so each of the three themes below spreads
// in its own copy of these rules. Factored out rather than triplicated, since only the colours
// differ: the editable editor is dark text on white, the two read-only ones are light text on a
// dark card. .cm-searchMatch itself already has a rule in each theme; this adds the gold for
// whichever match you are currently on, with a dark label so it stays readable on that gold.
function searchPanelTheme({ panelBg, panelFg, fieldBg, fieldBorder }) {
  return {
    ".cm-panels": {
      "backgroundColor": panelBg,
      "color": panelFg,
      "fontFamily": "inherit"
    },
    ".cm-panels-top": {
      "borderBottom": "1px solid " + fieldBorder
    },
    ".cm-panels-bottom": {
      "borderTop": "1px solid " + fieldBorder
    },
    // The text fields and the next/previous/all buttons. Checkboxes are excluded so this text-
    // field treatment doesn't turn them into empty bordered boxes; they instead pick up the
    // site-wide themed square that mainDesktop.scss gives every input[type="checkbox"], which
    // nothing in this theme competes with.
    ".cm-panel.cm-search input:not([type=checkbox]), .cm-panel.cm-search button": {
      "backgroundColor": fieldBg,
      "color": panelFg,
      "border": "1px solid " + fieldBorder,
      "borderRadius": "3px",
      "padding": "2px 6px",
      "fontFamily": "inherit"
    },
    // The checkbox labels ("match case", "regexp", "by word") need their colour stated rather
    // than inherited from .cm-panels: mainDesktop.scss carries a `*` colour rule, and a rule that
    // matches the element always beats an inherited value, however low its specificity. Without
    // this the labels come out light-on-light and vanish in the editable editor's panel.
    ".cm-panel.cm-search label": {
      "color": panelFg
    },
    ".cm-panel.cm-search button:hover": {
      "backgroundColor": $color_gold,
      "color": $color_dark,
      "cursor": "pointer"
    },
    // Sits after the shared button rule so the close "x" stays a bare glyph rather than picking
    // up the bordered-button treatment.
    ".cm-panel.cm-search [name=close]": {
      "backgroundColor": "transparent",
      "border": "none",
      "color": panelFg,
      "fontSize": "16px",
      "cursor": "pointer"
    },
    ".cm-searchMatch-selected": {
      "backgroundColor": $color_gold,
      "color": $color_dark
    }
  };
}

const themeDark = {
  ".cm-scroller": {
    "overflow": "auto"
  },
  ".cm-wrap": {
    "height": "100%"
  },
  "&": {
    "backgroundColor": $color_paper,
  },
  ".cm-content": {
    "caretColor": $color_ink,
  },
  ".cm-line": {
    "color": $color_ink
  },
  "&.cm-focused .cm-cursor": {
    "borderLeftColor": $color_ink
  },
  ".cm-activeLine": {
    "backgroundColor": translucent($color_ink, 10)
  },
  ".cm-searchMatch": {
    "backgroundColor": "#5f77b6"
  },
  ".cm-selectionBackground": {
    "backgroundColor": $color_gold_highlight + " !important",
  },
  ".cm-selectionBackground.cm-focused": {
    "backgroundColor": $color_gold_highlight + " !important",
  },
  ".cm-gutters": {
    "backgroundColor": $color_dark,
    "color": $color_white_l,
    "border": "none !important"
  },
  ".cm-selectionLayer": {
    "zIndex": 2 + " !important",
    "pointerEvents": "none"
  },
  ...searchPanelTheme({
    panelBg: `color-mix(in srgb, ${$color_ink} 12%, ${$color_paper})`,
    panelFg: $color_ink,
    fieldBg: $color_paper,
    fieldBorder: $color_white_l
  }),
}
const highlightStylesDark = HighlightStyle.define([

  {
    tag: [tags.keyword],
    color: syntaxPaper('keyword')
  }
  ,

  {
    tag: [tags.function(tags.variableName)],
    color: syntaxPaper('function')
  }
  ,

  {
    tag: [tags.variableName],
    color: syntaxPaper('variable')
  }
  ,

  {
    tag: [tags.brace],
    color: $color_ink
  }
  ,

  {
    tag: [tags.annotation],
    color: syntaxPaper('annotation')
  }
  ,

  {
    tag: [tags.typeName],
    color: syntaxPaper('type')
  }
  ,

  {
    tag: [tags.className],
    color: syntaxPaper('type')
  }
  ,

  {
    tag: [tags.operator, tags.operatorKeyword],
    color: $color_ink
  }
  ,

  {
    tag: [tags.squareBracket],
    color: $color_ink
  }
  ,

  {
    tag: [tags.angleBracket],
    color: $color_ink
  }
])

const themeLight = {
  ".cm-scroller": {
    "overflow": "auto",
    "scrollbarWidth": "none",     // Firefox
    "-msOverflowStyle": "none",   // IE/Edge legacy
  },
  ".cm-scroller::-webkit-scrollbar": {
    "display": "none"             // Chrome/Safari/Edge
  },
  ".cm-wrap": {
    "height": "100%"
  },
  "&": {
    "background": "none"
  },
  ".cm-content": {
    "caretColor": $color_white,
  },
  ".cm-line": {
    "color": $color_white,
    "padding-left": "0px"
  },
  "&.cm-focused .cm-cursor": {
    "borderLeftColor": $color_white
  },
  ".cm-activeLine": {
    "background": "none"
  },
  ".cm-searchMatch": {
    "backgroundColor": "#5f77b6"
  },
  ".cm-selectionBackground": {
    "backgroundColor": $color_gold_highlight + " !important",
  },
  ".cm-selectionBackground.cm-focused": {
    "backgroundColor": $color_gold_highlight + " !important",
  },
  ".cm-gutters": {
    "background": "none",
    "color": $color_white_l,
    "border": "none !important"
  },
  ".cm-selectionLayer": {
    "zIndex": 2 + " !important",
    "pointerEvents": "none"
  },
  ...searchPanelTheme({
    panelBg: $color_dark_h,
    panelFg: $color_white,
    fieldBg: $color_dark_b,
    fieldBorder: $color_white_l
  }),
}

const themeLightLarge = {
  ".cm-scroller": {
    "overflow": "auto"
  },
  ".cm-wrap": {
    "height": "auto"
  },
  "&": {
    "background": "none",
    "height": "auto"
  },
  ".cm-content": {
    "caretColor": $color_white,
  },
  ".cm-line": {
    "color": $color_white
  },
  "&.cm-focused .cm-cursor": {
    "borderLeftColor": $color_white
  },
  ".cm-activeLine": {
    "backgroundColor": translucent($color_white_l)
  },
  ".cm-searchMatch": {
    "backgroundColor": "#5f77b6"
  },
  ".cm-selectionBackground": {
    "backgroundColor": $color_gold_highlight + " !important",
  },
  ".cm-selectionBackground.cm-focused": {
    "backgroundColor": $color_gold_highlight + " !important",
  },
  ".cm-gutters": {
    "background": $color_dark_h,
    "color": $color_white,
    "border": "none !important"
  },
  ".cm-selectionLayer": {
    "zIndex": 2 + " !important",
    "pointerEvents": "none"
  },
  ...searchPanelTheme({
    panelBg: $color_dark_h,
    panelFg: $color_white,
    fieldBg: $color_dark_b,
    fieldBorder: $color_white_l
  }),
}

const highlightStylesLight = HighlightStyle.define([
  {
    tag: [tags.meta],
    color: $color_white // color for 'C++'
  },
  {
    tag: [tags.literal, tags.inserted],
    color: syntax('literal')
  },
  {
    tag: [tags.atom,
    tags.bool,
    tags.url,
    tags.contentSeparator,
    tags.labelName],
    color: $color_white
  },
  {
    tag: [tags.keyword],
    color: syntax('keyword')
  }
  ,

  {
    tag: [tags.function(tags.variableName)],
    color: syntax('function')
  }
  ,

  {
    tag: [tags.variableName],
    color: syntax('variable')
  }
  ,

  {
    tag: [tags.brace],
    color: $color_white
  }
  ,

  {
    tag: [tags.annotation],
    color: syntax('annotation')
  }
  ,

  {
    tag: [tags.typeName],
    color: syntax('type')
  }
  ,

  {
    tag: [tags.className],
    color: syntax('type')
  }
  ,

  {
    tag: [tags.operator, tags.operatorKeyword],
    color: $color_white
  }
  ,

  {
    tag: [tags.squareBracket],
    color: $color_white
  }
  ,

  {
    tag: [tags.angleBracket],
    color: $color_white
  }
])

export const CMThemeEditable = [
  EditorView.theme(themeDark),
  syntaxHighlighting(highlightStylesDark)
]

export const CMThemeReadOnly = [
  EditorView.theme(themeLight),
  syntaxHighlighting(highlightStylesLight)
]

export const CMThemeReadOnlyLarge = [
  EditorView.theme(themeLightLarge),
  syntaxHighlighting(highlightStylesLight)
]